import is from '@sindresorhus/is';

const DEFAULT_PADDING_PX = 8;

export type TileScrollAlign = 'auto' | 'start' | 'center' | 'end';

// Returns null when no scroll needed (align: 'auto' and tile already visible).
// Align: auto|start|end|center — TanStack Virtual semantics.
//
// `endReservedWidth` shrinks the effective scrollport from the right edge.
// Use it to reserve space for sticky-right elements (e.g. a docked chat) so
// `align: 'end'` parks tiles next to — not beneath — the sticky overlay.
export const computeTargetScrollLeft = (
    tileEl: HTMLElement,
    containerEl: HTMLElement,
    align: TileScrollAlign,
    padding: number = DEFAULT_PADDING_PX,
    endReservedWidth: number = 0,
): number | null => {
    const tileRect = tileEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const currentLeft = containerEl.scrollLeft;

    const tileLeftInContent = tileRect.left - containerRect.left + currentLeft;
    const tileRightInContent = tileLeftInContent + tileRect.width;
    const containerWidth = containerRect.width;
    const effectiveContainerWidth = Math.max(0, containerWidth - endReservedWidth);

    const alignStart = tileLeftInContent - padding;
    const alignEnd = tileRightInContent - effectiveContainerWidth + padding;
    const alignCenter = tileLeftInContent - (effectiveContainerWidth - tileRect.width) / 2;

    if (align === 'auto') {
        const fullyVisible =
            tileLeftInContent >= currentLeft && tileRightInContent <= currentLeft + effectiveContainerWidth;

        if (fullyVisible) {
            return null;
        }

        if (tileRect.width >= effectiveContainerWidth) {
            return alignStart;
        }

        if (tileLeftInContent < currentLeft) {
            return alignStart;
        }

        return alignEnd;
    }

    if (align === 'start') {
        return alignStart;
    }

    if (align === 'end') {
        return alignEnd;
    }

    return alignCenter;
};

/** @deprecated Use `useTilingScroll`. */
export const scrollTileHorizontally = (
    tileElement: HTMLElement | null,
    scrollContainer: HTMLElement | null,
    scrollBehavior: ScrollBehavior = 'smooth',
): void => {
    if (is.nullOrUndefined(tileElement) || is.nullOrUndefined(scrollContainer)) {
        return;
    }

    const target = computeTargetScrollLeft(tileElement, scrollContainer, 'end');

    if (is.null(target)) {
        return;
    }

    scrollContainer.scrollTo({
        behavior: scrollBehavior,
        left: target,
    });
};

/** @deprecated Use `useTilingScroll().scrollToTile(id, { align: 'end' })`. */
export const scrollToVisibleTile = (
    tileElement: HTMLElement | null,
    scrollContainer: HTMLElement | null,
    tileIndex: number,
    minIndexToScroll: number = 2,
): void => {
    if (tileIndex >= minIndexToScroll) {
        scrollTileHorizontally(tileElement, scrollContainer);
    }
};

/** @deprecated Use `useTilingScroll().scrollToTile(id, { align: 'end' })`. */
export const scrollToExpandedTile = (
    tileElement: HTMLElement | null,
    scrollContainer: HTMLElement | null,
    isExpanded: boolean | undefined,
): void => {
    if (isExpanded === true) {
        scrollTileHorizontally(tileElement, scrollContainer);
    }
};
